import { StyleSheet, Platform } from 'react-native'
import type { Theme } from '../../../theme/getTheme'
import { makeBoxShadow } from '../../../shared/helpers/shadowStyles.helper'

export function getAccountAdminPublicMarketingStyles(theme: Theme) {
  const heroBg = theme.background.primary
  const cardBg = theme.background.card
  const cardElevatedBg = theme.background.cardElevated
  const borderColor = theme.gray[200]
  const textPrimary = theme.gray[900]
  const textSecondary = theme.gray[500]
  const primaryColor = theme.primary
  const accentColor = theme.accent

  return StyleSheet.create({
    scroll: {
      flex: 1,
      backgroundColor: heroBg,
      ...(Platform.OS === 'web' && {
        minHeight: 0 as never,
      }),
    },
    scrollContent: {
      flexGrow: 1,
      paddingBottom: 64,
    },
    heroBand: {
      position: 'relative',
      paddingHorizontal: 24,
      paddingTop: Platform.OS === 'web' ? 74 : 44,
      paddingBottom: 58,
      overflow: 'hidden',
      backgroundColor: heroBg,
    },
    bgBlob: {
      position: 'absolute',
      borderRadius: 999,
      opacity: 0.6,
    },
    bgBlobPrimary: {
      top: -170,
      right: -130,
      width: 520,
      height: 520,
      backgroundColor: `${primaryColor}22`,
    },
    bgBlobGold: {
      bottom: -180,
      left: -90,
      width: 380,
      height: 380,
      backgroundColor: `${accentColor}1A`,
    },
    heroShell: {
      width: '100%',
      maxWidth: 1240,
      alignSelf: 'center',
      flexDirection: Platform.OS === 'web' ? 'row' : 'column',
      alignItems: 'center',
      gap: Platform.OS === 'web' ? 56 : 28,
      zIndex: 1,
    },
    heroShellStacked: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 34,
    },
    heroCopy: {
      flex: Platform.OS === 'web' ? 1 : undefined,
      maxWidth: 620,
    },
    heroCopyStacked: {
      maxWidth: 720,
      alignSelf: 'center',
      alignItems: 'center',
    },
    heroPanel: {
      width: Platform.OS === 'web' ? 390 : '100%',
      maxWidth: 430,
      borderRadius: 28,
      borderWidth: 1,
      borderColor: borderColor,
      backgroundColor: cardBg,
      padding: 28,
      overflow: 'hidden',
      ...makeBoxShadow({
        shadowColor: primaryColor,
        shadowOffset: { width: 0, height: 22 },
        shadowOpacity: 0.2,
        shadowRadius: 38,
      }),
    },
    heroPanelStacked: {
      width: '100%',
      maxWidth: 560,
      alignSelf: 'center',
    },
    heroPanelTopline: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 5,
      backgroundColor: accentColor,
    },
    heroPanelIcon: {
      width: 56,
      height: 56,
      borderRadius: 18,
      backgroundColor: `${primaryColor}1A`,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 18,
    },
    heroPanelTitle: {
      fontFamily:
        Platform.OS === 'web'
          ? '"Space Grotesk", "Inter", system-ui, sans-serif'
          : undefined,
      fontSize: 23,
      lineHeight: 29,
      fontWeight: '800',
      color: textPrimary,
      letterSpacing: -0.45,
      marginBottom: 10,
    },
    heroPanelBody: {
      fontSize: 15,
      lineHeight: 23,
      color: textSecondary,
    },
    heroBadge: {
      alignSelf: 'flex-start',
      paddingVertical: 6,
      paddingHorizontal: 12,
      borderRadius: 999,
      backgroundColor: `${primaryColor}1A`,
      borderWidth: 1,
      borderColor: `${primaryColor}40`,
      marginBottom: 16,
    },
    heroBadgeText: {
      fontSize: 12,
      fontWeight: '800',
      color: primaryColor,
      letterSpacing: 0.8,
      textTransform: 'uppercase',
    },
    heroTitle: {
      fontFamily:
        Platform.OS === 'web'
          ? '"Space Grotesk", "Inter", system-ui, sans-serif'
          : undefined,
      fontSize: Platform.OS === 'web' ? 58 : 34,
      fontWeight: '800',
      color: textPrimary,
      letterSpacing: -1.35,
      marginBottom: 16,
      lineHeight: Platform.OS === 'web' ? 64 : 40,
    },
    heroTitleStacked: {
      textAlign: 'center',
      fontSize: 46,
      lineHeight: 52,
      maxWidth: 680,
    },
    heroLead: {
      fontSize: Platform.OS === 'web' ? 17 : 16,
      lineHeight: Platform.OS === 'web' ? 28 : 24,
      color: textSecondary,
      maxWidth: 580,
    },
    heroLeadStacked: {
      textAlign: 'center',
      maxWidth: 620,
    },
    heroActions: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
      marginTop: 28,
    },
    heroActionsStacked: {
      justifyContent: 'center',
    },
    section: {
      paddingHorizontal: 24,
      paddingTop: 52,
      maxWidth: 1240,
      width: '100%',
      alignSelf: 'center',
    },
    sectionTitle: {
      fontFamily:
        Platform.OS === 'web'
          ? '"Space Grotesk", "Inter", system-ui, sans-serif'
          : undefined,
      fontSize: 34,
      lineHeight: 40,
      fontWeight: '800',
      color: textPrimary,
      letterSpacing: -0.8,
      marginBottom: 26,
      textAlign: 'center',
    },
    valueGrid: {
      flexDirection: Platform.OS === 'web' ? 'row' : 'column',
      flexWrap: 'wrap',
      gap: 16,
      justifyContent: 'center',
    },
    valueCard: {
      flex: Platform.OS === 'web' ? 1 : undefined,
      minWidth: Platform.OS === 'web' ? 260 : undefined,
      maxWidth: Platform.OS === 'web' ? 380 : undefined,
      backgroundColor: cardBg,
      borderRadius: 24,
      padding: 28,
      borderWidth: 1,
      borderColor: borderColor,
      ...makeBoxShadow({
        shadowColor: primaryColor,
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.12,
        shadowRadius: 22,
      }),
    },
    valueIconWrap: {
      width: 44,
      height: 44,
      borderRadius: 15,
      backgroundColor: `${primaryColor}1A`,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 12,
    },
    valueCardTitle: {
      fontSize: 17,
      fontWeight: '800',
      color: textPrimary,
      marginBottom: 8,
    },
    valueCardBody: {
      fontSize: 15,
      lineHeight: 22,
      color: textSecondary,
    },
    howList: {
      gap: 14,
      maxWidth: 640,
      alignSelf: 'center',
      width: '100%',
    },
    howRow: {
      flexDirection: 'row',
      gap: 14,
      alignItems: 'flex-start',
    },
    howIndex: {
      width: 28,
      height: 28,
      borderRadius: 14,
      backgroundColor: primaryColor,
      alignItems: 'center',
      justifyContent: 'center',
    },
    howIndexText: {
      fontSize: 14,
      fontWeight: '800',
      color: theme.onPrimary,
    },
    howText: {
      flex: 1,
      fontSize: 15,
      lineHeight: 22,
      color: textSecondary,
      paddingTop: 2,
    },
    primaryBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      backgroundColor: primaryColor,
      paddingVertical: 14,
      paddingHorizontal: 24,
      borderRadius: 16,
      minWidth: 140,
    },
    primaryBtnPressed: {
      opacity: 0.9,
    },
    primaryBtnHovered: {
      opacity: 0.95,
    },
    primaryBtnText: {
      color: theme.onPrimary,
      fontSize: 16,
      fontWeight: '700',
    },
    secondaryBtn: {
      backgroundColor: cardElevatedBg,
      borderWidth: 2,
      borderColor: primaryColor,
      paddingVertical: 14,
      paddingHorizontal: 24,
      borderRadius: 16,
      minWidth: 140,
      alignItems: 'center',
    },
    secondaryBtnPressed: {
      opacity: 0.9,
    },
    secondaryBtnHovered: {
      opacity: 0.95,
    },
    secondaryBtnText: {
      color: primaryColor,
      fontSize: 16,
      fontWeight: '700',
    },
  })
}
